import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { ShieldAlert, AlertCircle, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import {
  DEFAULT_INPUTS,
  NUMERIC_FIELD_SPECS,
  VOLTAGE_PRESETS_LV,
  CONDUCTOR_MATERIALS,
  INSULATION_RATINGS,
  LV_FUSE_FAMILIES,
  EV_HV_FUSE_FAMILIES,
  AWG_LABEL_BY_MM2,
  CELSIUS_TO_FAHRENHEIT,
  FAHRENHEIT_TO_CELSIUS,
  M_TO_FT,
  validateField,
} from '../../lib/fuseRatingTools';
import { fuseRatingToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;
const SPEC_BY_KEY = Object.fromEntries(NUMERIC_FIELD_SPECS.map((s) => [s.key, s]));
const TEMP_FIELD_KEYS = ['ambientTempWireC', 'ambientTempFuseC', 'customInsulationMaxTempC'];

const round1 = (n) => Math.round(n * 10) / 10;

const sizeLabel = (mm2, sizeDisplay) => {
  if (sizeDisplay === 'awg') {
    const awg = AWG_LABEL_BY_MM2[mm2];
    return awg ? `${awg} (${mm2} mm²)` : `${mm2} mm²`;
  }
  return `${mm2} mm²`;
};

function activeKeys(mode, inputMode, insulationRating) {
  const keys = [
    'systemVoltageV', 'wireLengthM', 'maxVoltageDropPercent', 'availableFaultCurrentA',
    'marginFactor', 'bundleSize', 'ambientTempWireC', 'ambientTempFuseC',
  ];
  keys.push(inputMode === 'power' ? 'loadPowerW' : 'loadCurrentA');
  if (insulationRating === 'custom') keys.push('customInsulationMaxTempC');
  if (mode === 'ev_hv') keys.push('breakingCapacityKa');
  return keys;
}

const FuseRatingCalculator = () => {
  const [mode, setMode] = useState(DEFAULT_INPUTS.mode);
  const [inputMode, setInputMode] = useState(DEFAULT_INPUTS.inputMode);
  const [conductorMaterial, setConductorMaterial] = useState(DEFAULT_INPUTS.conductorMaterial);
  const [insulationRating, setInsulationRating] = useState(DEFAULT_INPUTS.insulationRating);
  const [lvFuseFamily, setLvFuseFamily] = useState(DEFAULT_INPUTS.lvFuseFamily);
  const [evHvFuseFamily, setEvHvFuseFamily] = useState(DEFAULT_INPUTS.evHvFuseFamily);

  const [voltagePresetLv, setVoltagePresetLv] = useState('12');
  const [lengthUnit, setLengthUnit] = useState('m');
  const [tempUnit, setTempUnit] = useState('c');
  const [sizeDisplay, setSizeDisplay] = useState('mm2');

  // Canonical raw values -- always stored in the backend's base unit (V, A, W, m, degC,
  // %, count) regardless of which unit toggle is showing. LengthField/TempField below
  // convert only at render/onChange time so validation always runs against the spec's
  // (single, base-unit) min/max.
  const [raw, setRaw] = useState({
    systemVoltageV: String(DEFAULT_INPUTS.systemVoltageV),
    loadCurrentA: String(DEFAULT_INPUTS.loadCurrentA),
    loadPowerW: String(DEFAULT_INPUTS.loadPowerW),
    wireLengthM: String(DEFAULT_INPUTS.wireLengthM),
    customInsulationMaxTempC: String(DEFAULT_INPUTS.customInsulationMaxTempC),
    bundleSize: String(DEFAULT_INPUTS.bundleSize),
    ambientTempWireC: String(DEFAULT_INPUTS.ambientTempWireC),
    ambientTempFuseC: String(DEFAULT_INPUTS.ambientTempFuseC),
    maxVoltageDropPercent: String(DEFAULT_INPUTS.maxVoltageDropPercent),
    availableFaultCurrentA: String(DEFAULT_INPUTS.availableFaultCurrentA),
    marginFactor: String(DEFAULT_INPUTS.marginFactorLv),
    breakingCapacityKa: String(DEFAULT_INPUTS.breakingCapacityKa),
  });
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  // Switching mode resets the safety margin to that mode's default (still editable
  // afterwards) and, for LV, forces the voltage preset back to a sane default so an
  // EV/HV custom voltage isn't silently carried over into LV mode.
  useEffect(() => {
    setField('marginFactor', String(mode === 'lv' ? DEFAULT_INPUTS.marginFactorLv : DEFAULT_INPUTS.marginFactorEvHv));
    if (mode === 'lv') {
      setVoltagePresetLv('12');
      setField('systemVoltageV', '12');
    } else {
      setField('systemVoltageV', '400');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (mode === 'lv' && voltagePresetLv !== 'custom') {
      setField('systemVoltageV', voltagePresetLv);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voltagePresetLv]);

  const keys = useMemo(() => activeKeys(mode, inputMode, insulationRating), [mode, inputMode, insulationRating]);

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const key of keys) {
      const err = validateField(SPEC_BY_KEY[key], raw[key]);
      if (err) errors[key] = err;
      else parsed[key] = Number(raw[key]);
    }
    return { fieldErrors: errors, values: parsed };
  }, [keys, raw]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasErrors) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const payload = {
      mode,
      inputMode,
      conductorMaterial,
      insulationRating,
      systemVoltageV: values.systemVoltageV,
      wireLengthM: values.wireLengthM,
      loadCurrentA: values.loadCurrentA,
      loadPowerW: values.loadPowerW,
      bundleSize: values.bundleSize,
      ambientTempWireC: values.ambientTempWireC,
      ambientTempFuseC: values.ambientTempFuseC,
      maxVoltageDropPercent: values.maxVoltageDropPercent,
      availableFaultCurrentA: values.availableFaultCurrentA,
      marginFactor: values.marginFactor,
    };
    if (insulationRating === 'custom') payload.customInsulationMaxTempC = values.customInsulationMaxTempC;
    if (mode === 'lv') payload.lvFuseFamily = lvFuseFamily;
    else {
      payload.evHvFuseFamily = evHvFuseFamily;
      payload.breakingCapacityKa = values.breakingCapacityKa;
    }

    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      fuseRatingToolApi.calculate(payload)
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasErrors, values, mode, inputMode, conductorMaterial, insulationRating,
    lvFuseFamily, evHvFuseFamily,
  ]);

  const numField = (key, { placeholder } = {}) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    return (
      <div className="space-y-2" key={key}>
        <Label htmlFor={`fuse-${key}`} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {spec.label} {spec.unit && <span className="normal-case text-muted-foreground/70">({spec.unit})</span>}
        </Label>
        <Input
          id={`fuse-${key}`}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? `fuse-${key}-error` : undefined}
          data-testid={`fuse-${key}-input`}
        />
        {error && (
          <p id={`fuse-${key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`fuse-${key}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  // Temperature field: canonical value stays in raw[key] as degC; this only converts
  // what's displayed/typed when tempUnit is 'f'.
  const tempField = (key, label) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    const canonicalC = raw[key];
    const displayValue = tempUnit === 'f' && canonicalC !== '' && !Number.isNaN(Number(canonicalC))
      ? String(round1(CELSIUS_TO_FAHRENHEIT(Number(canonicalC))))
      : canonicalC;
    const onChange = (e) => {
      const typed = e.target.value;
      if (tempUnit === 'f') {
        if (typed === '' || typed === '-') { setField(key, typed); return; }
        const n = Number(typed);
        setField(key, Number.isNaN(n) ? typed : String(FAHRENHEIT_TO_CELSIUS(n)));
      } else {
        setField(key, typed);
      }
    };
    return (
      <div className="space-y-2" key={key}>
        <Label htmlFor={`fuse-${key}`} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {label} <span className="normal-case text-muted-foreground/70">(°{tempUnit.toUpperCase()})</span>
        </Label>
        <Input
          id={`fuse-${key}`}
          type="number"
          step="any"
          value={displayValue}
          onChange={onChange}
          className="font-mono"
          aria-invalid={!!error}
          data-testid={`fuse-${key}-input`}
        />
        {error && (
          <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {error}</p>
        )}
        {!error && key !== 'customInsulationMaxTempC' && (
          <p className="text-xs text-muted-foreground">Valid range {spec.min}–{spec.max}°C.</p>
        )}
      </div>
    );
  };

  const isEvHv = mode === 'ev_hv';

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="technical-card lg:col-span-3">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-autoxium-blue" />
                Fuse Rating Calculator
              </CardTitle>
              <CardDescription>
                Recommend wire gauge and fuse rating for a protected circuit, with
                voltage-drop, ampacity, breaking-capacity, and coordination checks.
                Results update live as you edit.
              </CardDescription>
            </div>
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList data-testid="fuse-mode-tabs">
                <TabsTrigger value="lv" data-testid="fuse-mode-lv">LV Automotive</TabsTrigger>
                <TabsTrigger value="ev_hv" data-testid="fuse-mode-evhv">EV / HV</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4 p-4 bg-muted/50 rounded-md">
            <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Circuit &amp; Load
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {mode === 'lv' ? (
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">System Voltage</Label>
                  <div className="flex gap-2">
                    <Select value={voltagePresetLv} onValueChange={setVoltagePresetLv}>
                      <SelectTrigger className="w-28" data-testid="fuse-voltage-preset-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {VOLTAGE_PRESETS_LV.map((v) => <SelectItem key={v} value={String(v)}>{v} V</SelectItem>)}
                        <SelectItem value="custom">Custom…</SelectItem>
                      </SelectContent>
                    </Select>
                    {voltagePresetLv === 'custom' && (
                      <Input
                        type="number"
                        step="any"
                        value={raw.systemVoltageV}
                        onChange={(e) => setField('systemVoltageV', e.target.value)}
                        className="font-mono"
                        data-testid="fuse-systemVoltageV-input"
                      />
                    )}
                  </div>
                  {fieldErrors.systemVoltageV && (
                    <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.systemVoltageV}</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="fuse-systemVoltageV" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    System Voltage <span className="normal-case text-muted-foreground/70">(V)</span>
                  </Label>
                  <Input
                    id="fuse-systemVoltageV"
                    type="number"
                    step="any"
                    value={raw.systemVoltageV}
                    onChange={(e) => setField('systemVoltageV', e.target.value)}
                    className="font-mono"
                    placeholder="e.g. 400"
                    aria-invalid={!!fieldErrors.systemVoltageV}
                    data-testid="fuse-systemVoltageV-input"
                  />
                  {fieldErrors.systemVoltageV ? (
                    <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.systemVoltageV}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Typically 200–900 V for EV/HV packs.</p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Specify Load As</Label>
                <Select value={inputMode} onValueChange={setInputMode}>
                  <SelectTrigger data-testid="fuse-input-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Load Current (A)</SelectItem>
                    <SelectItem value="power">Load Power (W)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {inputMode === 'power' ? numField('loadPowerW') : numField('loadCurrentA')}
              <div className="space-y-2">
                <Label htmlFor="fuse-wireLengthM" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Wire Length, One-Way <span className="normal-case text-muted-foreground/70">({lengthUnit})</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="fuse-wireLengthM"
                    type="number"
                    step="any"
                    value={lengthUnit === 'ft' && raw.wireLengthM !== '' ? String(round1(Number(raw.wireLengthM) * M_TO_FT)) : raw.wireLengthM}
                    onChange={(e) => {
                      const typed = e.target.value;
                      if (lengthUnit === 'ft') {
                        if (typed === '' || typed === '-') { setField('wireLengthM', typed); return; }
                        const n = Number(typed);
                        setField('wireLengthM', Number.isNaN(n) ? typed : String(n / M_TO_FT));
                      } else {
                        setField('wireLengthM', typed);
                      }
                    }}
                    className="font-mono"
                    aria-invalid={!!fieldErrors.wireLengthM}
                    data-testid="fuse-wireLengthM-input"
                  />
                  <Select value={lengthUnit} onValueChange={setLengthUnit}>
                    <SelectTrigger className="w-20" data-testid="fuse-length-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="m">m</SelectItem>
                      <SelectItem value="ft">ft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {fieldErrors.wireLengthM && (
                  <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.wireLengthM}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Conductor Material</Label>
                <Select value={conductorMaterial} onValueChange={setConductorMaterial}>
                  <SelectTrigger data-testid="fuse-material-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDUCTOR_MATERIALS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-4 bg-muted/50 rounded-md">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Conductor &amp; Ambient
              </h3>
              <Select value={tempUnit} onValueChange={setTempUnit}>
                <SelectTrigger className="w-24 h-8 text-xs" data-testid="fuse-temp-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="c">°C</SelectItem>
                  <SelectItem value="f">°F</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Insulation / Temp Rating</Label>
                <Select value={insulationRating} onValueChange={setInsulationRating}>
                  <SelectTrigger data-testid="fuse-insulation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INSULATION_RATINGS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {numField('bundleSize', { placeholder: 'e.g. 1' })}
              {insulationRating === 'custom' && tempField('customInsulationMaxTempC', 'Custom Insulation Max Temp')}
              {tempField('ambientTempWireC', 'Ambient Temp at Wire')}
              {tempField('ambientTempFuseC', 'Ambient Temp at Fuse')}
            </div>
          </div>

          <div className="space-y-4 p-4 bg-muted/50 rounded-md">
            <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Voltage Drop &amp; Fault Protection
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {numField('maxVoltageDropPercent')}
              {numField('availableFaultCurrentA')}
              {numField('marginFactor')}
              <p className="text-xs text-muted-foreground self-center pt-5">
                Default {mode === 'lv' ? '1.25' : '1.5'}× for {mode === 'lv' ? 'LV Automotive' : 'EV/HV'} — multiplies
                continuous load current before rounding up to a standard fuse rating.
              </p>
            </div>
          </div>

          <div className="space-y-4 p-4 bg-muted/50 rounded-md" data-testid="fuse-family-section">
            <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Fuse Selection
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {mode === 'lv' ? (
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Fuse Family</Label>
                  <Select value={lvFuseFamily} onValueChange={setLvFuseFamily}>
                    <SelectTrigger data-testid="fuse-lv-family-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LV_FUSE_FAMILIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Fuse Family</Label>
                    <Select value={evHvFuseFamily} onValueChange={setEvHvFuseFamily}>
                      <SelectTrigger data-testid="fuse-evhv-family-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EV_HV_FUSE_FAMILIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fuse-breakingCapacityKa" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Required Interrupt / Breaking Capacity <span className="normal-case text-muted-foreground/70">(kA)</span>
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input
                      id="fuse-breakingCapacityKa"
                      type="number"
                      step="any"
                      value={raw.breakingCapacityKa}
                      onChange={(e) => setField('breakingCapacityKa', e.target.value)}
                      className="font-mono"
                      aria-invalid={!!fieldErrors.breakingCapacityKa}
                      data-testid="fuse-breakingCapacityKa-input"
                    />
                    {fieldErrors.breakingCapacityKa && (
                      <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.breakingCapacityKa}</p>
                    )}
                    <p className="flex items-start gap-1.5 text-xs text-amber-700" data-testid="fuse-breaking-capacity-warning">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      Mandatory for HV circuits — a fuse rated below the available fault
                      current may fail to safely interrupt an arc, risking fire or
                      equipment damage (see ISO 6469-3).
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="lg:col-span-2 space-y-4">
        {calcError ? (
          <Card className="technical-card"><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-destructive" data-testid="fuse-calc-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
            </div>
          </CardContent></Card>
        ) : hasErrors ? (
          <Card className="technical-card"><CardContent className="pt-6">
            <p className="text-sm text-muted-foreground" data-testid="fuse-results-placeholder">
              Fix the highlighted fields to see a recommendation.
            </p>
          </CardContent></Card>
        ) : !results ? (
          <Card className="technical-card"><CardContent className="pt-6">
            <p className="text-sm text-muted-foreground" data-testid="fuse-results-loading">Calculating…</p>
          </CardContent></Card>
        ) : (
          <Results results={results} sizeDisplay={sizeDisplay} setSizeDisplay={setSizeDisplay} mode={mode} marginFactor={values.marginFactor} />
        )}
      </div>
    </div>
  );
};

const CheckBadge = ({ ok, label, testId }) => (
  <Badge
    variant="outline"
    className={`font-mono text-[10px] gap-1 ${ok ? 'text-emerald-700 border-emerald-600/40' : 'text-destructive border-destructive/40'}`}
    data-testid={testId}
  >
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
    {label}
  </Badge>
);

const Results = ({ results, sizeDisplay, setSizeDisplay, mode, marginFactor }) => {
  const { wire, fuse, checks, warnings } = results;
  const recIndex = wire.table.findIndex((r) => r.sizeMm2 === wire.recommendedSizeMm2);
  const windowLo = Math.max(0, recIndex - 2);
  const windowHi = Math.min(wire.table.length - 1, recIndex + 2);
  const nearbyTable = wire.table.slice(windowLo, windowHi + 1);

  return (
    <div className="space-y-4">
      <Card className="technical-card ring-1 ring-autoxium-blue/30" data-testid="fuse-results">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Recommended Wire &amp; Fuse</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Wire Gauge</p>
              <p className="font-mono text-2xl font-black text-autoxium-blue" data-testid="fuse-recommended-wire">
                {sizeLabel(wire.recommendedSizeMm2, sizeDisplay)}
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Fuse Rating</p>
              <p className="font-mono text-2xl font-black text-autoxium-blue" data-testid="fuse-recommended-fuse">
                {fuse.nominalRatingA} A
              </p>
              <p className="text-xs text-muted-foreground">{fuse.familyLabel}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <CheckBadge ok={checks.ampacity} label="Ampacity" testId="fuse-badge-ampacity" />
            <CheckBadge ok={checks.voltageDrop} label="Voltage Drop" testId="fuse-badge-vdrop" />
            <CheckBadge ok={checks.breakingCapacity} label="Breaking Capacity" testId="fuse-badge-breaking" />
            <CheckBadge ok={checks.coordination} label="Coordination" testId="fuse-badge-coordination" />
          </div>

          {(wire.exceedsStandardRangeVoltageDrop || wire.exceedsStandardRangeAmpacity || fuse.exceedsStandardRange) && (
            <div className="flex items-start gap-2 text-xs text-destructive font-bold" data-testid="fuse-exceeds-range-warning">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              {fuse.exceedsStandardRange
                ? "Required current exceeds this fuse family's largest standard rating."
                : "Even the largest standard wire size doesn't fully satisfy this circuit."}
            </div>
          )}

          {warnings.length > 0 && (
            <div className="space-y-1.5 rounded-md bg-destructive/5 border border-destructive/20 p-3" data-testid="fuse-warnings">
              {warnings.map((w, i) => (
                <p key={i} className="flex items-start gap-1.5 text-xs text-destructive leading-relaxed">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w}
                </p>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-structure text-xs font-mono">
            <div className="flex justify-between"><span className="text-muted-foreground">Load Current</span><span className="font-bold">{results.loadCurrentA} A</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">VDrop</span><span className="font-bold">{wire.voltageDropPercent}% ({wire.voltageDropV} V)</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Power Loss</span><span className="font-bold">{wire.powerLossW} W</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Effective Fuse Rating</span><span className="font-bold">{fuse.effectiveRatingA} A</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Breaking Capacity</span><span className="font-bold">{fuse.breakingCapacityKa} kA</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Wire Ampacity (derated)</span><span className="font-bold">{wire.ampacityA} A</span></div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-muted-foreground">Size table units</span>
            <Select value={sizeDisplay} onValueChange={setSizeDisplay}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="fuse-size-display-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mm2">mm²</SelectItem>
                <SelectItem value="awg">AWG / kcmil (nearest)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="technical-card">
        <Accordion type="single" collapsible>
          <AccordionItem value="why" className="border-none">
            <CardHeader className="py-0">
              <AccordionTrigger className="hover:no-underline py-4" data-testid="fuse-why-toggle">
                <CardTitle className="font-chivo uppercase tracking-tight text-sm">Why This Result</CardTitle>
              </AccordionTrigger>
            </CardHeader>
            <AccordionContent>
              <CardContent className="pt-0 space-y-2 text-xs text-muted-foreground leading-relaxed">
                <div className="flex justify-between"><span>Bundle derate ({mode === 'lv' ? 'wire bundle' : 'harness bundle'})</span><span className="font-mono font-bold text-foreground">×{wire.bundleFactor}</span></div>
                <div className="flex justify-between"><span>Ambient/insulation temp derate (wire)</span><span className="font-mono font-bold text-foreground">×{wire.temperatureFactor}</span></div>
                <div className="flex justify-between"><span>Fuse ambient-temp derate</span><span className="font-mono font-bold text-foreground">×{fuse.ambientDerateFactor}</span></div>
                <div className="flex justify-between"><span>Safety/margin factor applied to load current</span><span className="font-mono font-bold text-foreground">×{marginFactor} → {(results.loadCurrentA * marginFactor).toFixed(1)} A target, rounded up to {fuse.nominalRatingA} A standard rating</span></div>
                <p className="pt-2 border-t border-structure">
                  Wire size satisfies both the derated ampacity check and the voltage-drop
                  check ({wire.governingConstraint === 'ampacity' ? 'ampacity governs' : 'voltage drop governs'}
                  ) — the larger of the two candidate sizes is recommended. Fuse nominal
                  rating = load current × margin factor, rounded up to the nearest standard
                  rating in the selected family, then derated by ambient temperature at the
                  fuse to get the effective rating shown above.
                </p>
              </CardContent>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>

      <Card className="technical-card" data-testid="fuse-nearby-table">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-sm">Nearby Wire Sizes</CardTitle>
          <CardDescription>Margin check either side of the recommended size.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Size</TableHead>
                <TableHead className="text-right">Ampacity (A)</TableHead>
                <TableHead className="text-right">VDrop %</TableHead>
                <TableHead className="text-right">Loss (W)</TableHead>
                <TableHead className="text-center">OK</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {nearbyTable.map((row) => {
                const ok = row.meetsVoltageDrop && row.meetsAmpacity;
                const isRecommended = row.sizeMm2 === wire.recommendedSizeMm2;
                return (
                  <TableRow key={row.sizeMm2} className={isRecommended ? 'bg-autoxium-blue/5' : ''} data-testid={`fuse-wire-row-${row.sizeMm2}`}>
                    <TableCell className="font-mono text-xs font-bold">{sizeLabel(row.sizeMm2, sizeDisplay)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{row.ampacityA}</TableCell>
                    <TableCell className={`text-right font-mono text-xs ${row.meetsVoltageDrop ? '' : 'text-destructive'}`}>{row.voltageDropPercent}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{row.powerLossW}</TableCell>
                    <TableCell className="text-center">
                      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" /> : <XCircle className="h-4 w-4 text-destructive inline" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default FuseRatingCalculator;
